import CloseIcon from '@mui/icons-material/Close'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import { StatusChip } from '../components/StatusChip'
import { CALENDAR_VIEW, COMMON, FEEDS, SOURCE_DRAWER_FEEDS } from '../labels'
import { longDate } from '../lib/format'
import type { SourceCell } from '../lib/types'
import { BAND_SOFT, FONT_MONO, INK, INK_FAINT, INK_SOFT, LINE } from '../theme'

interface Props {
  cell: SourceCell | null
  onClose: () => void
}

/**
 * Drill-down. Todo el detalle vive aquí para que la vista principal no se
 * sature — el brief pide justamente eso.
 */
export function SourceDrawer({ cell, onClose }: Props) {
  return (
    <Drawer anchor="right" open={Boolean(cell)} onClose={onClose}>
      {cell && (
        <Box sx={{ width: { xs: '100vw', sm: 400 }, height: '100%', bgcolor: 'background.paper' }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={1}
            sx={{ p: 3, pb: 2.5, borderBottom: `1px solid ${LINE}` }}
          >
            <Box>
              <Typography variant="h2">{cell.source_name}</Typography>
              <Box
                component="span"
                sx={{
                  display: 'block',
                  fontFamily: FONT_MONO,
                  fontSize: '0.6875rem',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: INK_FAINT,
                  mt: 0.5,
                  mb: 1.5,
                }}
              >
                {cell.period_label}
              </Box>
              <StatusChip value={cell.status} />
            </Box>
            <IconButton size="small" onClick={onClose} aria-label={COMMON.close}>
              <CloseIcon sx={{ fontSize: 18, color: INK_SOFT }} />
            </IconButton>
          </Stack>

          <Box sx={{ p: 3 }}>
            {cell.status === 'check_failed' && (
              // La distinción que exige el brief, hecha explícita para el usuario.
              <Alert severity="warning" sx={{ mb: 2.5 }}>
                {cell.probe_error}
              </Alert>
            )}
            {cell.status === 'unconfigured' && (
              <Alert severity="info" sx={{ mb: 2.5 }}>
                {CALENDAR_VIEW.noPath}
              </Alert>
            )}

            <Stack divider={<Divider />}>
              <Row label={CALENDAR_VIEW.expected} value={longDate(cell.expected_date)} mono />
              <Row label={CALENDAR_VIEW.actual} value={longDate(cell.actual_date)} mono />
              <Row label={SOURCE_DRAWER_FEEDS} value={FEEDS[cell.feeds] ?? cell.feeds} />
              <Row label={CALENDAR_VIEW.owner} value={cell.data_owner ?? COMMON.none} />
              <Row label={CALENDAR_VIEW.custodian} value={cell.data_custodian ?? COMMON.none} />
              <Row
                label={CALENDAR_VIEW.steward}
                value={
                  [cell.data_steward, cell.secondary_steward].filter(Boolean).join(' y ') ||
                  COMMON.none
                }
              />
              {cell.file_name && <Row label={COMMON.file} value={cell.file_name} mono />}
              <Row
                label={CALENDAR_VIEW.lastChecked}
                value={
                  cell.last_checked_at
                    ? new Date(cell.last_checked_at).toLocaleString('es-MX')
                    : COMMON.none
                }
                mono
              />
            </Stack>

            {cell.detail && (
              <Box
                sx={{
                  mt: 2.5,
                  p: 2,
                  bgcolor: BAND_SOFT,
                  borderRadius: 3,
                  border: `1px solid ${LINE}`,
                }}
              >
                <Typography variant="body2" sx={{ color: INK_SOFT }}>
                  {cell.detail}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Drawer>
  )
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ py: 1.4 }}>
      <Box
        component="span"
        sx={{
          fontFamily: FONT_MONO,
          fontSize: '0.625rem',
          letterSpacing: '0.09em',
          textTransform: 'uppercase',
          color: INK_FAINT,
          flexShrink: 0,
          pt: 0.2,
        }}
      >
        {label}
      </Box>
      <Box
        component="span"
        sx={{
          textAlign: 'right',
          maxWidth: 230,
          fontSize: '0.8125rem',
          fontFamily: mono ? FONT_MONO : undefined,
          color: INK,
          wordBreak: 'break-word',
        }}
      >
        {value}
      </Box>
    </Stack>
  )
}
